/**
 * How an agreement appears on ridgewill.com/legal (Jon, 2026-10-15).
 *
 * ── The portal is the source, the website is a reader ────────────────
 *
 * ridgewill.com/legal renders whatever is published here, at Cloudflare's
 * edge, from a public read-only auth endpoint. So these fields are not a
 * description of the website; they ARE the website's table of contents. A
 * slug typed here is the address people bookmark, and changing it later
 * breaks every link to the old one.
 *
 * ── Two kinds, fixed at creation ─────────────────────────────────────
 *
 * An `agreement` is owed and accepted. A `notice` (Security, Accessibility,
 * Report an intimate image) is published and versioned the same way but is
 * never owed: publishing one fans out to nobody, and auth ignores
 * `requiresReacceptance` on it. Like the audience, the kind cannot be changed
 * after the agreement is created.
 */

export type AgreementKind = 'agreement' | 'notice';

export type PublicSection = 'ridgewill-terms' | 'ridgewill-privacy' | 'safety' | 'keyodo' | 'account';

/** In the order the website lists them. Labels are the site's own headings. */
export const PUBLIC_SECTIONS: { value: PublicSection; label: string }[] = [
  { value: 'ridgewill-terms', label: 'Ridgewill terms' },
  { value: 'ridgewill-privacy', label: 'Ridgewill privacy' },
  { value: 'safety', label: 'Safety and security' },
  { value: 'keyodo', label: 'Keyodo' },
  { value: 'account', label: 'Your account and this site' },
];

export function sectionLabel(section: string | null | undefined): string {
  if (!section) return 'No section';
  return PUBLIC_SECTIONS.find((s) => s.value === section)?.label ?? section;
}

/** The public fields as auth returns them on every agreement row. */
export type PublicFields = {
  publicSlug: string | null;
  publicSection: string | null;
  publicOrder: number;
  publicDescription: string;
  publicVersioned: boolean;
};

/**
 * The website's origin for THIS build: dev's Pages project or ridgewill.com,
 * baked in by deploy.yml like every other origin. Empty when the build has
 * none (staging has no site), and then the portal shows paths, not links.
 */
export const SITE_URL = (import.meta.env.VITE_SITE_URL ?? '').replace(/\/$/, '');

/**
 * Where one agreement, or one version of it, lives on the website. The
 * version form matches what auth writes into `documentUrl` when publishing
 * without one: `<site>/legal/<publicSlug>/<version>/`.
 */
export function publicUrl(base: string, publicSlug: string, version?: string): string {
  const path = `/legal/${publicSlug}/${version === undefined ? '' : `${encodeURIComponent(version)}/`}`;
  return `${base}${path}`;
}

/**
 * Why a typed web address would be refused, or null when auth will take it.
 * Checked here as well as there so the live URL preview never shows an
 * address that cannot exist.
 */
export function slugProblem(slug: string): string | null {
  if (!slug) return 'Give it a web address, or turn off Show on ridgewill.com.';
  if (!/^[a-z0-9-]+$/.test(slug)) return 'Lowercase letters, digits and hyphens only.';
  return null;
}

/** What the "On ridgewill.com" form holds while it is being edited. */
export type PublicDraft = {
  shown: boolean;
  slug: string;
  section: PublicSection | '';
  order: string;
  description: string;
  versioned: boolean;
};

export function draftFrom(a: PublicFields & { slug: string }): PublicDraft {
  return {
    shown: a.publicSlug !== null,
    // Off, the address field starts from the agreement's own slug, which is
    // nearly always what it should be called on the website too.
    slug: a.publicSlug ?? a.slug,
    section: (a.publicSection as PublicSection | null) ?? '',
    order: String(a.publicOrder),
    description: a.publicDescription,
    versioned: a.publicVersioned,
  };
}

export type PublicPatch = Partial<PublicFields>;

/**
 * The PATCH body: only the fields that changed, since auth needs at least one
 * and a field sent unchanged is still a write. Null when nothing changed, and
 * a string when the draft cannot be saved, so the Save button can say why.
 *
 * Turning the page off sends `publicSlug: null` and nothing else. The other
 * fields stay stored, so turning it back on restores the same entry.
 */
export function publicPatch(saved: PublicFields, d: PublicDraft): PublicPatch | null | string {
  const body: PublicPatch = {};
  if (!d.shown) {
    if (saved.publicSlug !== null) body.publicSlug = null;
    return Object.keys(body).length ? body : null;
  }

  const slug = d.slug.trim();
  const problem = slugProblem(slug);
  if (problem) return problem;
  const order = d.order.trim() === '' ? 0 : Number(d.order);
  if (!Number.isInteger(order)) return 'Order is a whole number.';

  if (slug !== saved.publicSlug) body.publicSlug = slug;
  const section = d.section || null;
  if (section !== saved.publicSection) body.publicSection = section;
  if (order !== saved.publicOrder) body.publicOrder = order;
  const description = d.description.trim();
  if (description !== saved.publicDescription) body.publicDescription = description;
  if (d.versioned !== saved.publicVersioned) body.publicVersioned = d.versioned;
  return Object.keys(body).length ? body : null;
}

/** Which field of the "On ridgewill.com" form a refusal belongs under. */
export function refusedPublicField(code: string | undefined): 'slug' | null {
  return code === 'PUBLIC_SLUG_TAKEN' ? 'slug' : null;
}
